import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { CrudApiBaseService } from '@core/api/crud-api.base';
import { PlanApiInterface } from '@core/interfaces/api/plan-api.interface';
import { Files } from '@core/interfaces/claims/files.interface';
import {
  IConfirmAddress,
  IConfirmEmail,
  IConfirmPhoneNumber,
  IVerifyCode,
} from '@core/interfaces/confirmation.interface';
import { PlanDetails, PlanLookup, Refund } from '@core/interfaces/plan/plan.interface';
import { ApplicationInsightsService } from '@core/services/application-insights/application-insights.service';
import { FileService } from '@core/services/file.service';
import { collectUploadEvidence } from '@core/utils/upload-evidence.util';
import { NotificationType } from '@shared/modules/notification/enums/notification-type.enum';
import { NotificationService } from '@shared/modules/notification/services/notification.service';

@Injectable({
  providedIn: 'root',
})
export class MyPlanApiService extends CrudApiBaseService<any> implements PlanApiInterface {
  endpoint = 'myplan';

  constructor(
    private notification: NotificationService,
    private readonly fileService: FileService,
    private readonly applicationInsightsService: ApplicationInsightsService,
  ) {
    super();
  }

  getPdf(planId: string): Observable<HttpResponse<Blob>> {
    return this.httpClient.get(this.getUrl(`${planId}/myplan.pdf`), { observe: 'response', responseType: 'blob' });
  }

  getFiles(id: number) {
    return this.fileService.getFileViewerWidth().pipe(
      switchMap(width =>
        this.httpClient
          .get<Omit<Files, 'index'>[]>(this.getUrl(`${id}/files`), {
          params: {
            width,
          },
        })
          .pipe(
            map(files =>
              this.fileService.sortFiles(files).map((file, index) => ({
                ...file,
                index,
              })),
            ),
          ),
      ),
    );
  }

  findMyPlan(planNumber: string) {
    return this.httpClient.get<PlanLookup>(this.getUrl(`findMyPlan/${planNumber}`));
  }

  detailed(id: number) {
    return this.httpClient.get<PlanDetails>(this.getUrl(`${id}/detailed`));
  }

  confirmationMail(data: IConfirmEmail) {
    return this.httpClient.post<boolean>(this.getUrl('confirmationmail'), data);
  }

  confirmPhoneNumber(data: IConfirmPhoneNumber) {
    return this.httpClient.post<PlanLookup & {message?: string}>(this.getUrl('confirm-phone-number'), data);
  }

  confirmAddress(data: IConfirmAddress) {
    return this.httpClient.post<PlanLookup & {message?: string}>(this.getUrl('confirm-address'), data);
  }

  verifycode(data: IVerifyCode) {
    return this.httpClient.post<PlanLookup>(this.getUrl('verifycode'), data);
  }

  me(): Observable<any> {
    return this.httpClient.get(this.getUrl('me'));
  }

  registerMyPlan(data: any, files: Files[]) {
    const model = JSON.stringify(data);
    const startedAt = Date.now();

    return from(collectUploadEvidence(files)).pipe(
      switchMap(({ evidence, unreadableFileNames }) => {
        this.applicationInsightsService.logEvent('ConsumerRegisterPlan', {
          modelLength: model.length,
          ...evidence,
        });

        if (unreadableFileNames.length > 0) {
          this.applicationInsightsService.logEvent('ConsumerRegisterPlanBlocked', {
            reason: 'unreadable-files',
            ...evidence,
          });
          this.notification.next({
            message:
              'Some of your files can no longer be read from your device: '
              + `${unreadableFileNames.join(', ')}. `
              + 'Please remove and re-attach them, then submit again.',
            type: NotificationType.Error,
            duration: 10000,
          });
          return throwError(() => new Error('Plan registration blocked: some files are no longer readable'));
        }

        const formData: FormData = new FormData();
        // model goes first so a truncated request body loses files, not the plan payload
        formData.append('model', model);
        for (let i = 0; i < files.length; i++) {
          formData.append('formfiles', files[i].originalFile);
        }

        return this.httpClient.post<boolean>(this.getUrl('registermyplan'), formData).pipe(
          tap(() => {
            this.applicationInsightsService.logEvent('ConsumerRegisterPlanSucceeded', {
              elapsedMs: Date.now() - startedAt,
              ...evidence,
            });
            this.notification.next({
              message: 'Your protection plan has been submitted for processing. You will be notified as soon as it has been located. During time of high volume, this could take a few minutes. If you don\'t receive a notification, please check back later to find it under MY PLANS.', // eslint-disable-line max-len
              type: NotificationType.Success,
              duration: 20000,
            });
          }),
          catchError((error: unknown) => {
            const httpError = error as HttpErrorResponse;
            this.applicationInsightsService.logEvent('ConsumerRegisterPlanFailed', {
              status: httpError?.status ?? -1,
              statusText: httpError?.statusText ?? '',
              serverMessage: `${JSON.stringify(httpError?.error ?? '')}`.slice(0, 256),
              elapsedMs: Date.now() - startedAt,
              modelLength: model.length,
              ...evidence,
            });
            return throwError(() => error);
          }),
        );
      }),
    );
  }

  refund(id: number | string): Observable<Refund> {
    return this.httpClient.get<Refund>(this.getUrl(`${id}/refund`));
  }

  cancelPlan(id: string, cancellationReason: any): Observable<any> {
    return this.httpClient.put(this.getUrl(`${id}/cancel`), cancellationReason);
  }

  getReceipt(consumerPlanName: string) {
    return this.fileService.getFileViewerWidth().pipe(
      switchMap(width =>
        this.httpClient.get<Files[]>(this.getUrl(`${consumerPlanName}/receipt`), {
          params: {
            width,
          },
        }),
      ),
    );
  }
}
